import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState } from 'react';

type AddFloorPlanProps = {
  label: string;
  onChange: (file: File | null) => Promise<void> | void;
  value?: File | null;
  onRemove?: () => void;
  errorText?: string;
  url?: string;
};

const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/png'];

export const AddFloorPlan = ({
  url,
  label,
  onChange,
  value,
  onRemove,
  errorText
}: AddFloorPlanProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileTypeError, setFileTypeError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const isPdf =
    value?.type === 'application/pdf' ||
    (!value && url?.toLowerCase().split('?')[0].endsWith('.pdf'));

  useEffect(() => {
    if (!value || value.type !== 'image/png') {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(value);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [value]);

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const handleFile = async (file: File | null) => {
    if (!file || isLoading) {
      return;
    }

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setFileTypeError('Only PDF and PNG files are allowed.');
      return;
    }

    setFileTypeError('');
    setIsLoading(true);

    try {
      await onChange(file);
    } catch (error) {
      console.error('Floor plan processing failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setFileTypeError('');

    if (inputRef.current) {
      inputRef.current.value = '';
    }

    onChange(null);
    onRemove?.();
  };

  const displayedError = fileTypeError || errorText;

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid #E0E0E0',
        borderRadius: 2,
        p: 2,
        position: 'relative'
      }}>
      <Typography mb={1.25} fontWeight={400} color={'#818181'}>
        {label}
      </Typography>

      {(value || url) && (
        <IconButton
          size="small"
          onClick={handleRemove}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1
          }}>
          <CloseIcon />
        </IconButton>
      )}

      <Box
        sx={{
          borderRadius: 2,
          border: displayedError
            ? '1px dashed red'
            : isDragging
              ? '2px dashed #1976d2'
              : '1px dashed #C5D0E6',
          height: 180,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          cursor: 'pointer',
          overflow: 'hidden',
          backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
        }}
        onClick={openFilePicker}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);

          const file = event.dataTransfer.files?.[0] || null;
          handleFile(file);
        }}>
        {value || url ? (
          isPdf ? (
            <>
              <PictureAsPdfIcon color="error" sx={{ fontSize: 55, mb: 1 }} />

              <Typography
                fontSize={14}
                fontWeight={500}
                px={2}
                color={'#818181'}
                textAlign="center"
                sx={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                {value?.name || 'Floor plan PDF'}
              </Typography>

              <Typography fontSize={12} color="text.secondary" mt={0.5}>
                Click to replace
              </Typography>
            </>
          ) : (
            <Box
              component="img"
              src={previewUrl || url}
              alt="Floor plan preview"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: 2
              }}
            />
          )
        ) : (
          <>
            <Typography fontSize={14} mb={1.5} color={'#818181'}>
              Upload floor plan
            </Typography>

            <IconButton size="large" color="primary" component="span">
              <UploadFileIcon sx={{ fontSize: 40 }} />
            </IconButton>

            <Typography fontSize={12} color="text.secondary">
              PDF or PNG only
            </Typography>
          </>
        )}

        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 3,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.88)'
            }}>
            <CircularProgress size={36} />

            <Typography fontSize={13} fontWeight={500}>
              Processing floor plan...
            </Typography>
          </Box>
        )}
      </Box>

      {displayedError && (
        <Typography fontSize={12} color="error" mt={1}>
          {displayedError}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,.pdf,.png"
        disabled={isLoading}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0] || null;
          handleFile(file);
        }}
      />
    </Box>
  );
};
